import { useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useAppShellCourseProgress from '@/data/hook/app-shell/useAppShellCourseProgress';
import useAuth from '@/data/hook/useAuth';
import usePreviousLocationPath from '@/data/hook/app-shell/usePreviousLocationPath';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import { rewardsQueryKeys } from '@/data/services/querykeys';
import { isStudent } from '@/functions/auth';
import Rewards from '@/models/Rewards';
import StudentDashboardStats from '@/models/StudentDashboard';
import User from '@/models/User';
import {
  AppShellNavItem,
  getAppShellNavItems,
} from '@/utils/app-shell/getAppShellNavItems';
import { ROUTES } from '@/utils/routes';

export type ShellBackTarget = {
  ownerId: string;
  href: string;
};

export type ShellBreadcrumbsTarget = {
  ownerId: string;
  items: BreadcrumbItem[];
};

export type AppShellState = {
  user: User;
  navItems: AppShellNavItem[];
  activeCourseProgress?: StudentDashboardStats;
  isLoadingCourse: boolean;
  rewards?: Rewards;
  isLoadingRewards: boolean;
  backTarget?: ShellBackTarget;
  breadcrumbsTarget?: ShellBreadcrumbsTarget;
  logout(): void;
};

export type AppShellActions = {
  setBackTarget: Dispatch<SetStateAction<ShellBackTarget | undefined>>;
  setBreadcrumbsTarget: Dispatch<
    SetStateAction<ShellBreadcrumbsTarget | undefined>
  >;
  getPreviousPath(): string | undefined;
};

type UseAppShellStateReturn = {
  state: AppShellState | null;
  actions: AppShellActions;
};

export default function useAppShellState(): UseAppShellStateReturn {
  const { user, logout } = useAuth();
  const [backTarget, setBackTarget] = useState<ShellBackTarget>();
  const [breadcrumbsTarget, setBreadcrumbsTarget] =
    useState<ShellBreadcrumbsTarget>();
  const getPreviousPath = usePreviousLocationPath();
  const studentId = user && isStudent(user.userType) ? user.id : undefined;

  const { activeCourseProgress, isLoading: isLoadingCourse } =
    useAppShellCourseProgress({ studentId });

  const { data: rewards, isInitialLoading: isLoadingRewards } = useQuery({
    ...rewardsQueryKeys.get(studentId ?? 0),
    enabled: !!studentId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const navItems = useMemo(() => {
    if (!user) return [];
    const lessonsHref = activeCourseProgress
      ? ROUTES.COURSES.INFO(
          activeCourseProgress.coursePath.slug,
          activeCourseProgress.klassId,
        )
      : undefined;
    return getAppShellNavItems({ userType: user.userType, lessonsHref });
  }, [user, activeCourseProgress]);

  const actions = useMemo(
    () => ({ setBackTarget, setBreadcrumbsTarget, getPreviousPath }),
    [setBackTarget, setBreadcrumbsTarget, getPreviousPath],
  );

  const state = useMemo(() => {
    if (!user || !logout) return null;
    return {
      user,
      navItems,
      activeCourseProgress,
      isLoadingCourse,
      rewards,
      isLoadingRewards,
      backTarget,
      breadcrumbsTarget,
      logout,
    };
  }, [
    user,
    logout,
    navItems,
    activeCourseProgress,
    isLoadingCourse,
    rewards,
    isLoadingRewards,
    backTarget,
    breadcrumbsTarget,
  ]);

  return { state, actions };
}
